import React from 'react';
import { Camera, ImagePlus, Package as PackageIcon, Trash2, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { useLanguage } from '../../../contexts/LanguageContext';
import { MAX_ITEMS } from '../usePackageForm';
import { volumeCbm } from '../pricing';

const MAX_PHOTOS_PER_ITEM = 5;

const PhotoPicker = ({ index, photos, onChange }) => {
  const { t, language } = useLanguage();
  const cameraId = `pkg-photo-camera-${index}`;
  const galleryId = `pkg-photo-gallery-${index}`;
  const full = photos.length >= MAX_PHOTOS_PER_ITEM;

  const addFiles = (files) => {
    const added = files.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    onChange([...photos, ...added].slice(0, MAX_PHOTOS_PER_ITEM));
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-xs text-muted-foreground">
        <Camera className="h-4 w-4" />
        {t('photos')} ({t('optional')}) — {photos.length}/{MAX_PHOTOS_PER_ITEM}
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button" variant="outline" size="sm" className="w-full sm:flex-1"
          disabled={full}
          onClick={() => document.getElementById(cameraId).click()}
        >
          <Camera className="h-4 w-4 mr-2 shrink-0" />
          <span className="truncate">{language === 'en' ? 'Take a photo' : 'Prendre une photo'}</span>
        </Button>
        <Button
          type="button" variant="outline" size="sm" className="w-full sm:flex-1"
          disabled={full}
          onClick={() => document.getElementById(galleryId).click()}
        >
          <ImagePlus className="h-4 w-4 mr-2 shrink-0" />
          <span className="truncate">{language === 'en' ? 'Gallery' : 'Galerie'}</span>
        </Button>
      </div>
      <input
        id={cameraId} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) addFiles([file]);
          e.target.value = '';
        }}
      />
      <input
        id={galleryId} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) addFiles(files);
          e.target.value = '';
        }}
      />
      {photos.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {photos.map((photo, i) => (
            <div key={i} className="relative">
              <img
                src={photo.preview}
                alt={photo.file?.name || `Photo ${i + 1}`}
                className="h-20 w-20 rounded-lg border object-cover"
              />
              <button
                type="button"
                onClick={() => onChange(photos.filter((_, j) => j !== i))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Étape 3 — un bloc par colis, tous traités de la même façon. */
const ItemsStep = ({
  form, errors = {}, onItem, onItemCount, onAddItem, onRemoveItem, merchandiseCategories = [], onField,
}) => {
  const { t, language } = useLanguage();
  const isMaritime = form.transport_mode === 'maritime';
  const count = form.items.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
        <Label className="shrink-0">{language === 'en' ? 'Number of packages' : 'Nombre de colis'}</Label>
        <div className="flex items-center gap-1">
          <Button
            type="button" variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onItemCount(count - 1)} disabled={count <= 1}
          >
            −
          </Button>
          <Input
            type="number" min="1" max={MAX_ITEMS} className="w-16 text-center"
            value={count} onChange={(e) => onItemCount(e.target.value)}
          />
          <Button
            type="button" variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onItemCount(count + 1)} disabled={count >= MAX_ITEMS}
          >
            +
          </Button>
        </div>
        <span className="text-xs text-muted-foreground">
          {language === 'en'
            ? 'Sender, route and payment are shared; describe each package below.'
            : 'Expéditeur, trajet et paiement sont communs ; décrivez chaque colis ci-dessous.'}
        </span>
      </div>

      {merchandiseCategories.length > 0 && (
        <div className="space-y-2">
          <Label>{language === 'en' ? 'Merchandise category' : 'Catégorie marchandise'}</Label>
          <select
            value={form.merchandise_category_id}
            onChange={(e) => onField('merchandise_category_id', e.target.value)}
            className="w-full sm:w-72 border border-border rounded-md px-3 py-2 text-sm bg-background"
          >
            <option value="">-- {t('none')} --</option>
            {merchandiseCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name} (+{cat.fixed_price} {cat.currency})
              </option>
            ))}
          </select>
        </div>
      )}

      {form.items.map((item, i) => (
        <div key={item.id} className="rounded-lg border border-border p-3 sm:p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <PackageIcon className="h-4 w-4 text-accent" />
              {language === 'en' ? 'Package' : 'Colis'} {i + 1}
            </h3>
            {count > 1 && (
              <button
                type="button"
                onClick={() => onRemoveItem(i)}
                className="text-muted-foreground hover:text-red-500"
                aria-label={language === 'en' ? 'Remove this package' : 'Retirer ce colis'}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('description')} *</Label>
              <Textarea
                value={item.description}
                onChange={(e) => onItem(i, 'description', e.target.value)}
                className={errors[`items.${i}.description`] ? 'border-red-500' : ''}
              />
              {errors[`items.${i}.description`] && (
                <p className="text-xs text-red-500">{errors[`items.${i}.description`]}</p>
              )}
            </div>

            {isMaritime ? (
              <div className="space-y-2">
                <Label>{language === 'en' ? 'Dimensions (cm)' : 'Dimensions (cm)'}</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="number" step="0.1" min="0" placeholder="L"
                    value={item.length_cm} onChange={(e) => onItem(i, 'length_cm', e.target.value)}
                  />
                  <Input
                    type="number" step="0.1" min="0" placeholder="l"
                    value={item.width_cm} onChange={(e) => onItem(i, 'width_cm', e.target.value)}
                  />
                  <Input
                    type="number" step="0.1" min="0" placeholder="H"
                    value={item.height_cm} onChange={(e) => onItem(i, 'height_cm', e.target.value)}
                  />
                </div>
                {volumeCbm(item) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Volume : {volumeCbm(item).toFixed(4)} CBM
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{t('weight')} (kg) *</Label>
                <Input
                  type="number" step="0.1" min="0"
                  value={item.weight}
                  onChange={(e) => onItem(i, 'weight', e.target.value)}
                  className={errors[`items.${i}.weight`] ? 'border-red-500' : ''}
                />
                {errors[`items.${i}.weight`] && (
                  <p className="text-xs text-red-500">{errors[`items.${i}.weight`]}</p>
                )}
              </div>
            )}
          </div>

          <PhotoPicker
            index={i}
            photos={item.photos || []}
            onChange={(photos) => onItem(i, 'photos', photos)}
          />
        </div>
      ))}

      <Button
        type="button" variant="outline" className="w-full"
        onClick={onAddItem} disabled={count >= MAX_ITEMS}
      >
        + {language === 'en' ? 'Add a package' : 'Ajouter un colis'}
      </Button>
    </div>
  );
};

export default ItemsStep;
